import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CTA, PageHero } from '../../components/Common';
import { Seo } from '../../components/Seo';
import { track } from '../../components/analytics';
import { productOffers } from '../../data/offers';
import {
  brokerExamples,
  categories,
  completeEdition,
  fleetExamples,
  formatInr,
  operatingSystems,
  perTruckPerDay,
  planPriceLines,
  plans,
  planTrucksLabel,
  pricing,
  pricingNotes,
  pricingPromises,
  quoteForTrucks,
  site,
  type PlanQuote,
} from '../../data/site';

/** One line under each plan's price: who it is for in practice. */
const PLAN_FITS: Record<(typeof plans)[number]['id'], string> = {
  free: 'For an owner with a truck or two: trips, LRs, invoices, party ledgers and the phone apps.',
  starter:
    'A flat price for a small fleet and its office: full accounting, settlements and GST documents.',
  business: 'A flat price for a growing fleet, with every office user and driver included.',
  fleet: 'Pay for the trucks you run. Each extra truck costs less as your fleet grows.',
  large: 'Volume pricing, onboarding and data migration planned with you.',
};

/** A typed count, read as a whole number; anything else is zero. */
const readCount = (value: string) => {
  const count = Number.parseInt(value, 10);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

/** A quote's two yearly prices, whatever its plan: a quoted plan's are its "from" prices. */
const quotePrices = (quote: PlanQuote) =>
  quote.plan === 'large'
    ? { essentials: quote.fromYearly, complete: quote.fromCompleteYearly as number | null }
    : { essentials: quote.yearly, complete: quote.completeYearly as number | null };

/** The smallest fleet on a plan, whose price is what its card leads with. */
const entryPrices = (plan: (typeof plans)[number]) =>
  quotePrices(quoteForTrucks(plan.minFleetSize));

/** A plan priced by the truck starts somewhere rather than costing one amount. */
const startsFrom = (id: (typeof plans)[number]['id']) => id === 'fleet' || id === 'large';

const planName = (id: (typeof plans)[number]['id']) => plans.find(plan => plan.id === id)?.name;

/** Trucks and hired trips in, the plan that fits and its price out. */
function PriceCalculator({
  trucks,
  hiredTrips,
  quote,
  onTrucks,
  onHiredTrips,
}: {
  trucks: string;
  hiredTrips: string;
  quote: PlanQuote;
  onTrucks: (value: string) => void;
  onHiredTrips: (value: string) => void;
}) {
  const prices = quotePrices(quote);
  const from = quote.plan === 'large' ? 'From ' : '';
  return (
    <div className="card price-calculator">
      <div>
        <h2 className="card-title">Work out your price</h2>
        <p>Tell us what you run. We count your own trucks, and your hired work by its trips.</p>
        <div className="price-calculator-fields">
          <label htmlFor="pricing-trucks">
            Your own trucks
            <input
              id="pricing-trucks"
              inputMode="numeric"
              min={0}
              type="number"
              value={trucks}
              onChange={event => onTrucks(event.target.value)}
            />
          </label>
          <label htmlFor="pricing-hired-trips">
            Hired trips a month
            <input
              id="pricing-hired-trips"
              inputMode="numeric"
              min={0}
              type="number"
              value={hiredTrips}
              onChange={event => onHiredTrips(event.target.value)}
            />
          </label>
        </div>
      </div>
      <div aria-live="polite" className="price-result">
        <p className="price-result-plan">{`Your plan: ${planName(quote.plan)}`}</p>
        {prices.complete === null ? (
          <div className="price-tile">
            <p className="price-amount">₹0</p>
            <p>Free, with no end date and no card.</p>
          </div>
        ) : (
          <div className="price-tiles">
            <div className="price-tile price-tile--lead">
              <p className="plan-edition">{`${completeEdition.name} (recommended)`}</p>
              <p className="price-amount">{`${from}${formatInr(prices.complete)}`}</p>
              <p>per year</p>
              <p className="plan-alternative">
                {`About ${perTruckPerDay(prices.complete, quote.fleetSize)} per truck per day`}
              </p>
            </div>
            <div className="price-tile">
              <p className="plan-edition">{completeEdition.essentialsName}</p>
              <p className="price-amount">{`${from}${formatInr(prices.essentials)}`}</p>
              <p>per year</p>
              <p className="plan-alternative">
                {`About ${perTruckPerDay(prices.essentials, quote.fleetSize)} per truck per day`}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** One table of what a few sizes come to: fleets by their trucks, brokers by their trips. */
function PriceTable({
  firstColumn,
  quotes,
  count,
}: {
  firstColumn: string;
  quotes: readonly PlanQuote[];
  count: (quote: PlanQuote) => number;
}) {
  return (
    <div className="pricing-table-wrap">
      <table className="pricing-table">
        <thead>
          <tr>
            <th scope="col">{firstColumn}</th>
            <th scope="col">Plan</th>
            <th scope="col">Complete per year</th>
            <th scope="col">Essentials per year</th>
            <th className="pricing-table-optional" scope="col">
              Complete per truck per day
            </th>
          </tr>
        </thead>
        <tbody>
          {quotes.map(quote => {
            const prices = quotePrices(quote);
            return (
              <tr key={count(quote)}>
                <td>{count(quote)}</td>
                <td>{planName(quote.plan)}</td>
                <td>{prices.complete === null ? '–' : formatInr(prices.complete)}</td>
                <td>{formatInr(prices.essentials)}</td>
                <td className="pricing-table-optional">
                  {prices.complete === null
                    ? '–'
                    : perTruckPerDay(prices.complete, quote.fleetSize)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function Pricing() {
  const [trucks, setTrucks] = useState('5');
  const [hiredTrips, setHiredTrips] = useState('0');
  const quote = quoteForTrucks(readCount(trucks), readCount(hiredTrips));
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: site.name,
    applicationCategory: categories.schemaOrg,
    operatingSystem: operatingSystems,
    description: site.description,
    publisher: { '@type': 'Organization', name: site.publisher },
    offers: productOffers(),
  };
  const free = plans[0];
  const yourSize = <span className="plan-your-size">Your size</span>;
  return (
    <>
      <Seo
        title="Transport software pricing, free for small fleets"
        breadcrumb="Pricing"
        description={`Transport Book is free for up to 2 trucks, then priced on your fleet, not per user: own trucks plus hired trips, so brokers pay for the trips they run. ${pricingNotes.gst} ${pricing.freeTrialDays}-day free trial.`}
        schema={schema}
      />
      <PageHero
        eyebrow="Pricing"
        title="Free while you're small. Pay when it's saving you real time."
        description="Priced on your fleet, never per user. Drivers on the driver app and everyone in the office are included, on the desktop, in the browser and on Android."
      />
      <section className="section pricing-section">
        <div className="container">
          <PriceCalculator
            hiredTrips={hiredTrips}
            quote={quote}
            trucks={trucks}
            onHiredTrips={setHiredTrips}
            onTrucks={setTrucks}
          />
        </div>
      </section>
      <section className="section pricing-section">
        <div className="container">
          <h2 className="section-title">Plans for every fleet size</h2>
          <article className={`card plan-free${quote.plan === 'free' ? ' plan-card--match' : ''}`}>
            <div>
              <h3 className="card-title">
                {free.name}
                {quote.plan === 'free' ? yourSize : null}
              </h3>
              <p className="plan-size">{planTrucksLabel(free.id)}</p>
            </div>
            <div>
              <p className="plan-free-price">{planPriceLines(free.id).price}</p>
              <p className="plan-alternative">{planPriceLines(free.id).alternative}</p>
            </div>
            <p className="plan-free-fits">{PLAN_FITS[free.id]}</p>
            <Link
              className="button secondary"
              to="/start-trial"
              onClick={() => track('trial_cta_click', { placement: `pricing_${free.id}` })}
            >
              Start free trial
            </Link>
          </article>
          <div className="pricing-plans">
            {plans.slice(1).map(plan => {
              const lines = planPriceLines(plan.id);
              const prices = entryPrices(plan);
              const from = startsFrom(plan.id) ? 'from ' : '';
              const isQuote = plan.id === 'large';
              const fits = quote.plan === plan.id;
              return (
                <article
                  className={`card plan-card${fits ? ' plan-card--match' : ''}`}
                  key={plan.id}
                >
                  <h3 className="card-title">
                    {plan.name}
                    {fits ? yourSize : null}
                  </h3>
                  <p className="plan-size">{planTrucksLabel(plan.id)}</p>
                  <p className="plan-edition">{`${completeEdition.name} (recommended)`}</p>
                  <p className="plan-price">
                    {from ? <span className="plan-from">{from}</span> : null}
                    {formatInr(prices.complete as number)}
                    <span className="plan-per"> / year</span>
                  </p>
                  <p className="plan-edition">{completeEdition.essentialsName}</p>
                  <p className="plan-price-second">
                    {from ? <span className="plan-from">{from}</span> : null}
                    {formatInr(prices.essentials)}
                    <span className="plan-per"> / year</span>
                  </p>
                  {plan.id === 'fleet' ? null : (
                    <p className="plan-alternative">{lines.alternative}</p>
                  )}
                  <p className="plan-fits">{PLAN_FITS[plan.id]}</p>
                  {startsFrom(plan.id) ? (
                    <details className="plan-workings">
                      <summary>How this adds up</summary>
                      <p>{`${completeEdition.name}: ${lines.complete}`}</p>
                      <p>{`${completeEdition.essentialsName}: ${lines.price}`}</p>
                      {plan.id === 'fleet' ? (
                        <p>{`Monthly: ${lines.alternative.replace(/^or /, '')}`}</p>
                      ) : null}
                    </details>
                  ) : null}
                  <div className="actions">
                    <Link
                      className={`button ${fits ? 'primary' : 'secondary'}`}
                      to="/start-trial"
                      onClick={() => track('trial_cta_click', { placement: `pricing_${plan.id}` })}
                    >
                      Start free trial
                    </Link>
                    {isQuote ? (
                      <Link
                        className="button secondary"
                        to="/request-demo"
                        onClick={() => track('demo_cta_click')}
                      >
                        Talk to us
                      </Link>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section pricing-section">
        <div className="container">
          <h2 className="section-title">Essentials or Complete</h2>
          <p className="pricing-lead">
            Essentials keeps your books. Complete does the work for you as well, for one fixed
            yearly price, and never charges for your records.
          </p>
          <div className="pricing-editions">
            <article className="card">
              <h3 className="card-title">{completeEdition.essentialsName}</h3>
              <ul className="pricing-checks">
                {completeEdition.essentialsIncludes.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="card pricing-edition-lead">
              <h3 className="card-title">{completeEdition.name}</h3>
              <p className="plan-alternative">{`Everything in ${completeEdition.essentialsName}, and:`}</p>
              <ul className="pricing-checks">
                {completeEdition.includes.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>
      <section className="section pricing-section">
        <div className="container">
          <div className="pricing-examples">
            <div>
              <h2 className="section-title">What it comes to</h2>
              <p className="pricing-lead">
                Each extra truck costs less as your fleet grows. Large fleets are from these prices.
              </p>
              <PriceTable
                count={example => example.trucks}
                firstColumn="Trucks"
                quotes={fleetExamples}
              />
            </div>
            <div>
              <h2 className="section-title">If you run hired trucks</h2>
              <p className="pricing-lead">
                Brokers pay for the trips they run, with no trucks of their own needed.
              </p>
              <PriceTable
                count={example => example.hiredTripsPerMonth}
                firstColumn="Hired trips a month"
                quotes={brokerExamples}
              />
            </div>
          </div>
        </div>
      </section>
      <section className="section pricing-section">
        <div className="container">
          <h2 className="section-title">What we promise you</h2>
          <div className="grid three">
            {pricingPromises.map(promise => (
              <article className="card" key={promise.title}>
                <h3 className="card-title">{promise.title}</h3>
                <p>{promise.body}</p>
              </article>
            ))}
          </div>
          <h3 className="pricing-notes-title">The details</h3>
          <ul className="pricing-notes">
            <li>{pricingNotes.gst}</li>
            <li>{pricingNotes.setupFee}</li>
            <li>{pricingNotes.perTruck}</li>
            <li>{pricingNotes.hiredTrips}</li>
            <li>{pricingNotes.complete}</li>
            <li>{pricingNotes.higherPlan}</li>
            <li>{pricingNotes.growth}</li>
            <li>{pricingNotes.trial}</li>
          </ul>
          <p className="pricing-legal">
            See the <Link to="/terms">terms of service</Link> and the{' '}
            <Link to="/refunds">refund policy</Link>.
          </p>
        </div>
      </section>
      <CTA
        title={`Try everything for ${pricing.freeTrialDays} days first`}
        body="Set up your organisation in a few minutes, with no approval and no card. Choose a plan when you know it fits."
      />
    </>
  );
}
